import type {
  DataResponse,
  Flow,
  FlowAuthorization,
  FlowRun,
  FlowRunSummary,
  FlowSummary,
  FlowTrigger,
  PaginatedResponse,
} from "@/types";
import { api } from "../api";

export const flowService = {
  list: (params?: { page?: number; pageSize?: number; visibility?: string; teamUuid?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    if (params?.visibility) searchParams.set("visibility", params.visibility);
    if (params?.teamUuid) searchParams.set("team_uuid", params.teamUuid);
    return api.get<PaginatedResponse<FlowSummary>>(`/flows?${searchParams}`);
  },
  get: (flowId: string) => api.get<DataResponse<Flow>>(`/flows/${flowId}`),
  create: (data: {
    name: string;
    description?: string;
    purpose?: string;
    visibility?: string;
    team_uuid?: string;
    steps?: Array<{
      agent_uuid?: string;
      step_type?: string;
      instructions?: string;
      expected_output?: string;
      config?: Record<string, unknown>;
      requires_review?: boolean;
    }>;
    connectors?: Array<{
      from_step_order?: number;
      to_step_order: number;
      label?: string;
    }>;
  }) => api.post<DataResponse<Flow>>("/flows", data),
  update: (
    flowId: string,
    data: {
      name?: string;
      description?: string;
      purpose?: string;
      visibility?: string;
      steps?: Array<{
        agent_uuid?: string;
        step_type?: string;
        instructions?: string;
        expected_output?: string;
        config?: Record<string, unknown>;
        requires_review?: boolean;
      }>;
      connectors?: Array<{
        from_step_order?: number;
        to_step_order: number;
        label?: string;
      }>;
    },
  ) => api.put<DataResponse<Flow>>(`/flows/${flowId}`, data),
  delete: (flowId: string) => api.delete(`/flows/${flowId}`),
  run: (flowId: string, input_prompt: string, input_values?: Record<string, string>) =>
    api.post<DataResponse<FlowRun>>(`/flows/${flowId}/runs`, { input_prompt, input_values }),
  /** Kick off a flow run — returns { operation_id } (HTTP 202). */
  runStream: (
    flowId: string,
    input_prompt: string,
    input_values?: Record<string, string>,
    file_blob_paths?: string[],
  ) =>
    api.post<{ operation_id: string }>(`/flows/${flowId}/runs/stream`, { input_prompt, input_values, file_blob_paths }),
  getRuns: (flowId: string, params?: { page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    return api.get<PaginatedResponse<FlowRunSummary>>(`/flows/${flowId}/runs?${searchParams}`);
  },
  getRun: (flowId: string, runId: string) => api.get<DataResponse<FlowRun>>(`/flows/${flowId}/runs/${runId}`),
  resumeRun: (flowId: string, runId: string, approval: boolean, modified_output?: string) =>
    api.post<DataResponse<FlowRun>>(`/flows/${flowId}/runs/${runId}/resume`, {
      approval,
      modified_output,
    }),
  cancelRun: (flowId: string, runId: string, reason?: string) =>
    api.post<DataResponse<FlowRun>>(`/flows/${flowId}/runs/${runId}/cancel`, { reason }),

  // Triggers
  getTriggers: (flowId: string) => api.get<DataResponse<FlowTrigger[]>>(`/flows/${flowId}/triggers`),
  createTrigger: (
    flowId: string,
    data: {
      trigger_type: string;
      name: string;
      description?: string;
      config?: Record<string, unknown>;
      schedule?: {
        schedule_type: string;
        timezone?: string;
        run_at_time?: string;
        days_of_week?: number[];
        interval_days?: number;
        start_date?: string;
      };
    },
  ) => api.post<DataResponse<FlowTrigger>>(`/flows/${flowId}/triggers`, data),
  deleteTrigger: (flowId: string, triggerId: string) => api.delete(`/flows/${flowId}/triggers/${triggerId}`),
  updateTrigger: (
    flowId: string,
    triggerId: string,
    data: {
      name?: string;
      description?: string;
      config?: Record<string, unknown>;
      schedule?: {
        schedule_type: string;
        timezone?: string;
        run_at_time?: string;
        days_of_week?: number[];
        interval_days?: number;
        start_date?: string;
      };
    },
  ) => api.put<DataResponse<FlowTrigger>>(`/flows/${flowId}/triggers/${triggerId}`, data),
  /** Upload files for use in a flow run. Returns blob paths. */
  uploadFiles: (flowId: string, files: File[]) =>
    api.upload<{ files: Array<{ blob_path: string; filename: string; mime_type: string; size: number }> }>(
      `/flows/${flowId}/upload`,
      files,
    ),
  /** Regenerate a webhook secret. Returns the new secret (shown once). */
  regenerateWebhookSecret: (webhookUuid: string) =>
    api.post<DataResponse<{ secret: string; message: string }>>(`/webhooks/${webhookUuid}/regenerate-secret`),

  // Background-access authorization (delegated token caching for scheduled flows)
  /** Get the current user's scheduled-flow background-access authorization status. */
  getAuthorization: (flowId: string) => api.get<DataResponse<FlowAuthorization>>(`/flows/${flowId}/authorization`),
  /** Explicitly (re-)authorize background delegated access for scheduled flows. */
  authorize: (flowId: string) => api.post<DataResponse<FlowAuthorization>>(`/flows/${flowId}/authorization`),
};
